"use client";

import {
  Anchor,
  Box,
  Button,
  Container,
  Divider,
  Group,
  List,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { useEffect } from "react";

import { NextLink } from "@/app/lib/components/next/NextLink";
import { ProtectedEmailLink } from "@/app/lib/components/ProtectedEmail";
import { CONTACT_EMAIL_BASE64 } from "@/app/lib/seo/siteConfig";

export default function SecurityPage() {
  // Print this page as a standalone document for security reviews: tag the body
  // so the print rules in global.css drop the marketing header and footer.
  useEffect(() => {
    document.body.classList.add("print-document");
    return () => document.body.classList.remove("print-document");
  }, []);

  const handlePrint = () => {
    if (typeof window !== "undefined") {
      window.print();
    }
  };

  return (
    <Container size="md" py="xl">
      <Stack gap="lg" px={{ base: "md", md: 0 }}>
        <Box mb="xl">
          <Group justify="space-between" align="flex-start" wrap="nowrap">
            <Box style={{ flex: 1 }}>
              <Title order={1} mb="xs">
                Security Overview
              </Title>
              <Text size="lg" c="gray.7">
                Last Updated: June 26, 2026
              </Text>
            </Box>
            <Button
              variant="default"
              onClick={handlePrint}
              className="print-hidden"
              style={{ flexShrink: 0 }}
            >
              Save as PDF
            </Button>
          </Group>
        </Box>

        <Text>
          Orgtools is a multi-tenant B2B platform that processes organizational data and decision
          records. Security and tenant isolation are designed into the product and infrastructure,
          not bolted on. This overview describes the controls we operate today and is shared with
          customers and prospects during security reviews.
        </Text>

        <Divider my="md" />

        <Box>
          <Title order={2} mb="md">
            Compliance Posture
          </Title>
          <Text>
            Orgtools operates a SOC 2-aligned security program. We have implemented the
            administrative, technical, and infrastructure controls expected of a SOC 2 Type II
            program, described throughout this document. We have not yet completed a formal
            third-party SOC 2 audit. As an early-stage company we have prioritized building and
            operating these controls; we will pursue formal attestation as we scale.
          </Text>
          <Text mt="md">
            Our security program was designed and is run by a founder who led SOC 2 compliance
            programs for more than a decade. We are happy to walk through any control in detail or
            complete a security questionnaire on request.
          </Text>
        </Box>

        <Divider my="md" />

        <Box>
          <Title order={2} mb="md">
            Infrastructure and Hosting
          </Title>
          <List withPadding spacing="xs">
            <List.Item>
              Hosted on Amazon Web Services (AWS) in the United States, on infrastructure defined
              and version-controlled as code (AWS CDK).
            </List.Item>
            <List.Item>
              Databases and internal services run in private subnets and are not publicly
              accessible. Network access is restricted by security groups so the database is
              reachable only from the application tier.
            </List.Item>
            <List.Item>
              Infrastructure changes are checked against automated security rules (cdk-nag) before
              deployment, with any exceptions documented.
            </List.Item>
            <List.Item>
              AWS account activity is recorded in an encrypted, multi-region CloudTrail audit log
              with log-file integrity validation enabled.
            </List.Item>
          </List>
        </Box>

        <Divider my="md" />

        <Box>
          <Title order={2} mb="md">
            Encryption
          </Title>
          <List withPadding spacing="xs">
            <List.Item>
              Data is encrypted in transit using TLS, with HSTS enforced (including preload) and
              secure, HTTP-only cookies.
            </List.Item>
            <List.Item>
              Data is encrypted at rest (AES-256) across our database, object storage, caches,
              message queues, secrets, and backups.
            </List.Item>
            <List.Item>
              Secrets and credentials are stored in AWS Secrets Manager, never in source code.
            </List.Item>
          </List>
        </Box>

        <Divider my="md" />

        <Box>
          <Title order={2} mb="md">
            Tenant Isolation
          </Title>
          <Text mb="xs">
            Multi-tenant isolation is enforced at the database level, not only in application code:
          </Text>
          <List withPadding spacing="xs">
            <List.Item>
              PostgreSQL Row-Level Security (RLS) scopes every query to a single tenant, so one
              customer's data is never returned to another.
            </List.Item>
            <List.Item>
              The application connects through a least-privilege database role limited to data
              operations. Schema changes use a separate, controlled path.
            </List.Item>
            <List.Item>
              Authorization inside the product is layered: tenant isolation, then role-based
              permissions, then per-object checks.
            </List.Item>
          </List>
        </Box>

        <Divider my="md" />

        <Box>
          <Title order={2} mb="md">
            Authentication and Access Control
          </Title>
          <List withPadding spacing="xs">
            <List.Item>
              Orgtools is passwordless. Users sign in through one-time magic links and email
              verification, so we store no user passwords and eliminate password reuse and
              credential-stuffing risk.
            </List.Item>
            <List.Item>Sessions use secure, HTTP-only cookies with CSRF protection.</List.Item>
            <List.Item>
              Administrative and infrastructure access (cloud console, source control, internal
              tooling, and AWS) is centralized through Google Workspace single sign-on, with
              multi-factor authentication enforced and hardened account security settings applied.
              Because identity is centralized, access can be granted or revoked across all connected
              systems in a single action.
            </List.Item>
            <List.Item>
              Access is granted on a least-privilege basis. IAM roles and database roles are scoped
              to the minimum access required.
            </List.Item>
          </List>
        </Box>

        <Divider my="md" />

        <Box>
          <Title order={2} mb="md">
            Application Security
          </Title>
          <List withPadding spacing="xs">
            <List.Item>
              Static application security testing (Bandit) runs in our pre-commit pipeline alongside
              linting and formatting checks.
            </List.Item>
            <List.Item>
              All changes go through code review, and our automated test suite runs in CI on every
              change.
            </List.Item>
            <List.Item>
              Dependencies are pinned to exact, reviewed versions across the backend and frontend to
              reduce supply-chain risk.
            </List.Item>
            <List.Item>
              Datastores receive automated minor-version security patches; we apply other security
              updates promptly.
            </List.Item>
          </List>
        </Box>

        <Divider my="md" />

        <Box>
          <Title order={2} mb="md">
            Logging, Monitoring, and Alerting
          </Title>
          <List withPadding spacing="xs">
            <List.Item>
              Application errors and performance are monitored with Sentry, and the platform emits
              structured logs.
            </List.Item>
            <List.Item>
              AWS API activity is captured in CloudTrail; database health uses enhanced monitoring
              and performance insights.
            </List.Item>
            <List.Item>
              CloudWatch alarms notify the team of operational issues (Ex: CPU, memory, storage,
              connection, and latency thresholds).
            </List.Item>
          </List>
        </Box>

        <Divider my="md" />

        <Box>
          <Title order={2} mb="md">
            AI Data Handling
          </Title>
          <Text>
            AI features run large language models within AWS Bedrock. Your content is processed
            inside that environment and is not sent to separate third-party model-provider APIs. The
            model providers whose models we use do not receive your content directly, retain it, or
            train on it, and we do not enable provider data-sharing or human-review options.
          </Text>
          <Text mt="md">
            We do not use your content to train foundation models. Our LLM observability is
            configured in production to record operational metadata only (Ex: model, timing, and
            errors) and to exclude the content of prompts and responses. See our{" "}
            <Anchor component={NextLink} href="/privacy">
              Privacy Policy
            </Anchor>{" "}
            and{" "}
            <Anchor component={NextLink} href="/subprocessors">
              Subprocessors
            </Anchor>{" "}
            page for details.
          </Text>
        </Box>

        <Divider my="md" />

        <Box>
          <Title order={2} mb="md">
            Backups and Resilience
          </Title>
          <List withPadding spacing="xs">
            <List.Item>
              The primary database uses automated, encrypted backups with point-in-time recovery.
            </List.Item>
            <List.Item>
              Deletion protection is enabled on the production database to prevent accidental
              destruction.
            </List.Item>
            <List.Item>
              Infrastructure is defined as code, so environments can be rebuilt reproducibly.
            </List.Item>
          </List>
        </Box>

        <Divider my="md" />

        <Box>
          <Title order={2} mb="md">
            Data Retention, Deletion, and Sub-processors
          </Title>
          <Text>
            We retain personal data only as long as needed to provide the Services and meet legal
            obligations, and customers may request deletion at any time. Our current list of
            sub-processors, with each one's purpose and location, is published and kept current. See
            our{" "}
            <Anchor component={NextLink} href="/privacy">
              Privacy Policy
            </Anchor>{" "}
            for retention details and our{" "}
            <Anchor component={NextLink} href="/subprocessors">
              Subprocessors
            </Anchor>{" "}
            page for the full list.
          </Text>
        </Box>

        <Divider my="md" />

        <Box>
          <Title order={2} mb="md">
            Incident Response
          </Title>
          <Text>
            We maintain procedures to detect, investigate, and respond to security incidents. In the
            event of a breach affecting personal data, we notify affected customers and relevant
            authorities as required by applicable law, typically within 72 hours of becoming aware
            of the incident.
          </Text>
        </Box>

        <Divider my="md" />

        <Box>
          <Title order={2} mb="md">
            Data Processing and Privacy
          </Title>
          <Text>
            Our practices are aligned with the GDPR and US state privacy laws including the CCPA. A
            Data Processing Agreement is available for customers who require one. Contact us to
            review or execute our{" "}
            <Anchor component={NextLink} href="/dpa">
              DPA
            </Anchor>
            .
          </Text>
        </Box>

        <Divider my="md" />

        <Box>
          <Title order={2} mb="md">
            Contact
          </Title>
          <Text>
            For security questions, to report a vulnerability, or to request a security review,
            contact <ProtectedEmailLink address={CONTACT_EMAIL_BASE64.security} />.
          </Text>
        </Box>
      </Stack>
    </Container>
  );
}
